import { html } from "htm/preact";
import { useRef, useEffect } from "preact/hooks";
import { openId } from "./state.js";
import { useSignalEffect } from "@preact/signals";

export default function NavButtonMobile() {
  const buttonRef = useRef(null);

  useSignalEffect(() => {
    const mobileNavButton = buttonRef.current;
    const header = document.querySelector("#header");
    const isOpen = header.hasAttribute("data-nav-mobile-open");
    if (openId.value !== "mobile-nav" && isOpen) {
      header.removeAttribute("data-nav-mobile-open");
      mobileNavButton.setAttribute("aria-expanded", "false");
    }
  })

  useEffect(() => {
    const mobileNavButton = buttonRef.current;
    const header = document.querySelector("#header");

    const toggleMobileNav = () => {
      const isOpen = header.hasAttribute("data-nav-mobile-open");

      if (isOpen) {
        header.removeAttribute("data-nav-mobile-open");
        document.body.removeAttribute("data-nav-mobile-open");
        // Optionally add aria-expanded for accessibility
        mobileNavButton.setAttribute("aria-expanded", "false");
      } else {
        openId.value = "mobile-nav";
        header.removeAttribute("data-nav-open");
        header.setAttribute("data-nav-mobile-open", "");
        document.body.setAttribute("data-nav-mobile-open", "");
        mobileNavButton.setAttribute("aria-expanded", "true");
      }
    };

    // Close mobile navigation
    const closeMobileNav = () => {
      header.removeAttribute("data-nav-mobile-open");
      document.body.removeAttribute("data-nav-mobile-open");
      mobileNavButton.setAttribute("aria-expanded", "false");
    };

    // Mobile nav button click handler
    mobileNavButton.addEventListener("click", (event) => {
      event.stopPropagation();
      toggleMobileNav();
    });

    // Close mobile nav on escape key
    document.addEventListener("keydown", (event) => {
      if (
        event.key === "Escape" &&
        header.hasAttribute("data-nav-mobile-open")
      ) {
        closeMobileNav();
      }
    });

    // Close mobile nav when window is resized to desktop size
    window.addEventListener("resize", () => {
      // Check if we're now in desktop mode (lg breakpoint is 1024px in Tailwind)
      if (
        window.innerWidth >= 1024 &&
        header.hasAttribute("data-nav-mobile-open")
      ) {
        closeMobileNav();
      }
    });

    // Initialize aria-expanded attribute
    mobileNavButton.setAttribute("aria-expanded", "false");
  });

  // Mobile accordion menu logic
  useEffect(() => {
    const toggleButtons = document.querySelectorAll(".mobile-nav-toggle");
    const header = document.querySelector("#header");

    if (!toggleButtons.length) {
      return;
    }

    // Click handler for accordion toggles
    const handleToggleClick = function (e) {
      e.preventDefault();
      e.stopPropagation();

      const targetId = this.getAttribute("data-target");
      const targetContent = document.getElementById(targetId);

      if (!targetContent) return;

      const isExpanded = this.getAttribute("aria-expanded") === "true";

      // Close all other sections
      document.querySelectorAll(".mobile-nav-content").forEach((content) => {
        if (content !== targetContent) {
          content.classList.add("hidden");
        }
      });

      document.querySelectorAll(".mobile-nav-toggle").forEach((button) => {
        if (button !== this) {
          button.setAttribute("aria-expanded", "false");
        }
      });

      // Toggle current section
      if (isExpanded) {
        targetContent.classList.add("hidden");
        this.setAttribute("aria-expanded", "false");
      } else {
        targetContent.classList.remove("hidden");
        this.setAttribute("aria-expanded", "true");
      }
    };

    // Attach event listeners
    toggleButtons.forEach((button) => {
      button.addEventListener("click", handleToggleClick);
      button.setAttribute("aria-expanded", "false");
    });

    // Observer to close all sections when mobile nav is closed
    let observer;
    if (header) {
      observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "data-nav-mobile-open"
          ) {
            if (!header.hasAttribute("data-nav-mobile-open")) {
              document.querySelectorAll(".mobile-nav-content").forEach((content) => {
                content.classList.add("hidden");
              });
              document.querySelectorAll(".mobile-nav-toggle").forEach((button) => {
                button.setAttribute("aria-expanded", "false");
              });
            }
          }
        });
      });

      observer.observe(header, {
        attributes: true,
        attributeFilter: ["data-nav-mobile-open"],
      });
    }

    // Cleanup function
    return () => {
      toggleButtons.forEach((button) => {
        button.removeEventListener("click", handleToggleClick);
      });
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  return html`<button
    ref=${buttonRef}
    type="button"
    class="p-3 flex justify-center items-center cursor-pointer group-data-hero/root:text-zinc-100 group-data-hero-outside:text-zinc-900 group-data-nav-mobile-open:text-zinc-900 group-data-nav-open:text-zinc-900 text-base touch-manipulation"
  >
    <span class="sr-only">yes</span>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1"
      class="size-4 stroke-current block group-data-nav-mobile-open:hidden"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
      />
    </svg>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="1"
      class="size-4 stroke-current hidden group-data-nav-mobile-open:block"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  </button>`;
}
